import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, AlertCircle, Phone, Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROJECT_TYPES = [
  'Bridge/Highway',
  'Marine/Coastal',
  'Parking Structure',
  'Industrial',
  'Residential',
  'Wastewater/Utility',
  'Other'
];

const PRODUCTS = [
  { id: 'geobar-b', name: 'GEObar B (BFRP)' },
  { id: 'geobar-fg', name: 'GEObar FG (GFRP)' },
  { id: 'geofabric', name: 'GEOfabric' },
  { id: 'technical-data', name: 'Technical Data Only' }
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    project_type: '',
    products_of_interest: [],
    quantity_timeline: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleProductToggle = (productId) => {
    setFormData(prev => ({
      ...prev,
      products_of_interest: prev.products_of_interest.includes(productId)
        ? prev.products_of_interest.filter(id => id !== productId)
        : [...prev.products_of_interest, productId]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.company.trim()) newErrors.company = 'Company is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    if (!formData.project_type) newErrors.project_type = 'Please select a project type';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API_URL}/api/contact`, formData);
      
      if (response.data.success) {
        setSubmitSuccess(true);
        toast.success('Your inquiry has been submitted successfully');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          project_type: '',
          products_of_interest: [],
          quantity_timeline: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="bg-charcoal min-h-screen pt-20" data-testid="contact-page">
        <section className="py-24 lg:py-32">
          <div className="max-w-2xl mx-auto px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-20 h-20 bg-bronze/20 flex items-center justify-center mx-auto mb-8">
                <CheckCircle size={40} className="text-bronze" />
              </div>
              <h1 className="heading-display text-stone text-4xl mb-6">
                THANK YOU
              </h1>
              <p className="text-stone/60 text-lg mb-8">
                Your inquiry has been received. Our team will respond within one business day.
              </p>
              <button
                onClick={() => setSubmitSuccess(false)}
                className="btn btn-outline"
                data-testid="submit-another-btn"
              >
                Submit Another Inquiry
              </button>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-charcoal min-h-screen pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 border-b border-border-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-bronze text-mono text-sm tracking-widest mb-4">GET IN TOUCH</p>
            <h1 className="heading-display text-stone text-5xl md:text-6xl mb-6">
              LET'S TALK MATERIALS.
            </h1>
            <p className="text-stone/60 max-w-2xl text-lg">
              Request technical data sheets, sample kits, or a project quote. Our team responds to all inquiries within one business day.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-2"
            >
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-stone text-sm mb-2">
                      Name <span className="text-bronze">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`input ${errors.name ? 'border-red-500' : ''}`}
                      placeholder="John Smith"
                      data-testid="input-name"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> {errors.name}
                      </p>
                    )}
                  </div>

                  {/* Company */}
                  <div>
                    <label htmlFor="company" className="block text-stone text-sm mb-2">
                      Company <span className="text-bronze">*</span>
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className={`input ${errors.company ? 'border-red-500' : ''}`}
                      placeholder="ABC Engineering"
                      data-testid="input-company"
                    />
                    {errors.company && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> {errors.company}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-stone text-sm mb-2">
                      Email <span className="text-bronze">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`input ${errors.email ? 'border-red-500' : ''}`}
                      placeholder="john@company.com"
                      data-testid="input-email"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-stone text-sm mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input"
                      placeholder="(555) 123-4567"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                {/* Project Type */}
                <div>
                  <label htmlFor="project_type" className="block text-stone text-sm mb-2">
                    Project Type <span className="text-bronze">*</span>
                  </label>
                  <select
                    id="project_type"
                    name="project_type"
                    value={formData.project_type}
                    onChange={handleChange}
                    className={`input ${errors.project_type ? 'border-red-500' : ''}`}
                    data-testid="select-project-type"
                  >
                    <option value="">Select project type...</option>
                    {PROJECT_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {errors.project_type && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={12} /> {errors.project_type}
                    </p>
                  )}
                </div>

                {/* Products of Interest */}
                <div>
                  <label className="block text-stone text-sm mb-3">
                    Products of Interest
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {PRODUCTS.map(product => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => handleProductToggle(product.id)}
                        className={`p-3 text-left text-sm border transition-colors ${
                          formData.products_of_interest.includes(product.id)
                            ? 'border-bronze bg-bronze/10 text-bronze'
                            : 'border-border-dark text-stone/60 hover:border-stone/30'
                        }`}
                        data-testid={`product-checkbox-${product.id}`}
                      >
                        {product.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quantity/Timeline */}
                <div>
                  <label htmlFor="quantity_timeline" className="block text-stone text-sm mb-2">
                    Approximate Quantity / Timeline
                  </label>
                  <input
                    type="text"
                    id="quantity_timeline"
                    name="quantity_timeline"
                    value={formData.quantity_timeline}
                    onChange={handleChange}
                    className="input"
                    placeholder="e.g., 500 #4 bars, needed by Q2 2024"
                    data-testid="input-quantity"
                  />
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-stone text-sm mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className="input resize-none"
                    placeholder="Tell us about your project..."
                    data-testid="input-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn btn-primary w-full md:w-auto"
                  data-testid="submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white border-t-transparent animate-spin mr-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Inquiry
                      <Send size={18} className="ml-2" />
                    </>
                  )}
                </button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="bg-surface-dark border border-border-dark p-8 sticky top-28">
                <h3 className="heading-section text-stone text-xl mb-6">Contact Information</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <Mail size={20} className="text-bronze flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-stone/50 text-xs uppercase tracking-wider mb-1">Email</p>
                      <a href="mailto:sales@standardcomposites.us" className="text-stone hover:text-bronze transition-colors">
                        sales@standardcomposites.us
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <Phone size={20} className="text-bronze flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-stone/50 text-xs uppercase tracking-wider mb-1">Phone</p>
                      <a href="tel:+15551234567" className="text-stone hover:text-bronze transition-colors">
                        (555) 123-4567
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <MapPin size={20} className="text-bronze flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-stone/50 text-xs uppercase tracking-wider mb-1">Address</p>
                      <p className="text-stone">
                        123 Industrial Parkway<br />
                        Suite 100<br />
                        Houston, TX 77001
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-border-dark">
                  <p className="text-stone/50 text-sm">
                    For urgent project timelines, please call directly. We respond to all inquiries within one business day.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
